import { ChangeEvent, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box, SelectChangeEvent } from '@mui/material';
import { AutocompleteOption, LwButton, LwFormInput, LwFormSelect } from 'redesign';
import { IconAdd } from 'assets/img';
import { generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DocumentsParams,
  documentStatusOptions,
  DocumentsToolboxForm,
  SetDocumentParams,
} from 'hq/components/views/documents/documents-page/documents-type';
import { useGetDocumentModels } from '../commons/hooks/use-get-document-models/use-get-document-models';

export const DocumentPageToolbox = ({
  params,
  setParams,
}: {
  params: DocumentsParams;
  setParams: SetDocumentParams;
}) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific', 'all-platforms-common']);
  const redirectPath = generatePath('issue');

  const form = useForm<DocumentsToolboxForm>({
    defaultValues: {
      name: params.name,
      documentType: params.documentType,
      status: params.status,
    },
  });

  const isDocumentStatus = (value: string) => {
    return value === 'Pending' || value === 'Accepted' || value === 'Signed';
  };

  const handleStatusChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;

      if (value === 'all') {
        return setParams({ status: undefined });
      }

      if (isDocumentStatus(value)) {
        return setParams({ status: value });
      }

      return setParams({ status: undefined });
    },
    [setParams]
  );

  const handleTypeChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const value = e.target.value;
      setParams({ documentType: value === 'all' ? undefined : value });
    },
    [setParams]
  );

  const handleNameSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setParams({ name: e.target.value });
    },
    [setParams]
  );

  const statusOptions: AutocompleteOption[] = useMemo(() => {
    const statusOpts = documentStatusOptions.map((status) => {
      return {
        value: status,
        label: t(`documents.status.${status.toLocaleLowerCase()}`, { ns: 'web-common' }),
      };
    });

    return [
      { value: 'all', label: t('dropdown.select_all', { ns: 'all-platforms-common' }) },
      ...statusOpts,
    ];
  }, [t, documentStatusOptions]);

  const documentModelsQuery = useGetDocumentModels();

  const availableDocuments: AutocompleteOption[] = useMemo(() => {
    const selectAll = {
      value: 'all',
      label: t('dropdown.select_all', { ns: 'all-platforms-common' }),
    };

    if (documentModelsQuery.status !== 'success') {
      return [selectAll];
    }

    const documentModelsResponse = documentModelsQuery.data;
    const docs = Object.keys(documentModelsResponse).map((key) => {
      return {
        value: key,
        label: documentModelsResponse[key].copy,
      };
    });

    return [selectAll, ...docs];
  }, [documentModelsQuery]);

  return (
    <>
      <Box display="flex" gap={5} flex={1}>
        <LwFormInput
          name="name"
          control={form.control}
          label={t('buttons.search', { ns: 'web-common' })}
          placeholder="Filter document op flexwerker naam"
          onChange={handleNameSearchChange}
        />
        <LwFormSelect
          control={form.control}
          name="status"
          label={t('table_headers.status', { ns: 'web-common' })}
          options={statusOptions}
          onChange={handleStatusChange}
        />
        <LwFormSelect
          control={form.control}
          name="documentType"
          label={t('table_headers.document_type', { ns: 'web-common' })}
          options={availableDocuments}
          onChange={handleTypeChange}
        />
      </Box>
      <Box display="flex" flex={1} justifyContent="flex-end">
        <LwButton to={redirectPath} startIcon={<IconAdd />} color="primary" iconColorMode="fill">
          {t('document_create.page_title', { ns: 'hq-screen-specific' })}
        </LwButton>
      </Box>
    </>
  );
};
